import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';
import { Eye, EyeOff, Lock, CheckCircle } from 'lucide-react';
import MintedLogo from '../components/common/MintedLogo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password })
      });
      const data = await res.json();
      if (res.ok) {
        setSuccess(true);
        toast.success('Password reset successfully!');
      } else {
        toast.error(data.detail || 'Failed to reset password');
      }
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-minted-800 via-minted-700 to-minted-800 flex flex-col items-center justify-center px-6">
        <MintedLogo size="lg" showText={true} glowEffect={true} />
        <div className="bg-white rounded-2xl p-6 mt-6 max-w-sm w-full text-center">
          <Lock className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h2 className="text-lg font-bold text-gray-900 mb-2">Invalid Reset Link</h2>
          <p className="text-gray-500 text-sm mb-4">This password reset link is invalid or has expired.</p>
          <Button onClick={() => navigate('/login')} className="w-full bg-minted-600 hover:bg-minted-700">
            Back to Login
          </Button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-minted-800 via-minted-700 to-minted-800 flex flex-col items-center justify-center px-6">
        <MintedLogo size="lg" showText={true} glowEffect={true} />
        <div className="bg-white rounded-2xl p-6 mt-6 max-w-sm w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-lg font-bold text-gray-900 mb-2">Password Reset!</h2>
          <p className="text-gray-500 text-sm mb-4">Your password has been updated. You can now sign in with your new password.</p>
          <Button
            onClick={() => navigate('/login')}
            className="w-full bg-minted-600 hover:bg-minted-700"
            data-testid="go-to-login-btn"
          >
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-minted-800 via-minted-700 to-minted-800 flex flex-col">
      <Toaster position="top-center" richColors />
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-8">
        <MintedLogo size="lg" showText={true} glowEffect={true} />
      </div>

      <div className="bg-white rounded-t-3xl px-6 py-8 shadow-xl">
        <div className="text-center mb-6">
          <Lock className="w-10 h-10 text-minted-600 mx-auto mb-2" />
          <h2 className="text-xl font-bold text-gray-900">Set New Password</h2>
          <p className="text-sm text-gray-500 mt-1">Enter your new password below</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="password">New Password</Label>
            <div className="relative mt-1">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="At least 6 characters"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                data-testid="new-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div>
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <Input
              id="confirmPassword"
              type="password"
              placeholder="Confirm your password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className="mt-1"
              data-testid="confirm-password-input"
            />
          </div>

          <Button
            type="submit"
            className="w-full bg-minted-600 hover:bg-minted-700 h-12 text-base"
            disabled={loading}
            data-testid="reset-password-submit-btn"
          >
            {loading ? 'Resetting...' : 'Reset Password'}
          </Button>
        </form>

        <p className="text-center text-gray-500 text-sm mt-4">
          <button onClick={() => navigate('/login')} className="text-minted-600 hover:underline">
            Back to Login
          </button>
        </p>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
